"use client";

import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
} from "./catalyst/alert";
import {
  MultipartFileUploader,
  OnUploadSuccess,
} from "./multipart-file-uploader";
import { useNotification } from "@/lib/hooks/use-notification";
import { toPublicBucketUrl } from "@/lib/r2-public-url";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { Button } from "./catalyst/button";

interface UploaderWrapperProps {
  aspectRatio: number;
  existingImageUrl?: string;
  onUpload: (url: string) => Promise<{ success: boolean; error?: string }>;
  onRemove: () => Promise<{ success: boolean; error?: string }>;
  children: (props: {
    currentImageUrl?: string;
    openUploadModal: () => void;
    clearImage: () => void;
  }) => React.ReactNode;
}

export function UploaderWrapper({
  aspectRatio,
  existingImageUrl,
  onUpload,
  onRemove,
  children,
}: Readonly<UploaderWrapperProps>) {
  const { showNotification } = useNotification();
  const t = useTranslations("UploaderWrapper");
  const [currentImageUrl, setCurrentImageUrl] = useState<string | undefined>(
    existingImageUrl,
  );

  useEffect(() => {
    setCurrentImageUrl(existingImageUrl);
  }, [existingImageUrl]);

  const handleUploadSuccess: OnUploadSuccess = async (result) => {
    try {
      const rawUrl = result.successful?.[0]?.uploadURL;
      const uploadUrl = toPublicBucketUrl(rawUrl);
      if (uploadUrl) {
        setCurrentImageUrl(uploadUrl);
      }
      if (uploadUrl) {
        const response = await onUpload(uploadUrl);
        if (response.success) {
          showNotification(t("imageUpdatedSuccess"), "success");
        } else {
          showNotification(response.error || t("failedToUpdateImage"), "error");
        }
      }
    } catch (error) {
      console.error("Error updating image:", error);
      showNotification(t("failedToUpdateImage"), "error");
    }
  };

  const handleImageRemove = async (clearFile: () => void) => {
    try {
      const response = await onRemove();
      if (response.success) {
        clearFile();
        setCurrentImageUrl(undefined);
        showNotification(t("imageRemovedSuccess"), "success");
        return true;
      }

      showNotification(response.error || t("failedToRemoveImage"), "error");
      return false;
    } catch (error) {
      console.error("Error removing image:", error);
      showNotification(t("failedToRemoveImage"), "error");
      return false;
    }
  };

  const restrictions = {
    allowedFileTypes: ["image/*"],
    maxNumberOfFiles: 1,
    maxFileSize: 10 * 1024 * 1024, // 10MB
  };

  return (
    <MultipartFileUploader
      existingUrl={currentImageUrl}
      onUploadSuccess={handleUploadSuccess}
      aspectRatio={aspectRatio}
      uploadType="image"
      restrictions={restrictions}
    >
      {({ currentUrl: uploaderUrl, openUploadModal, clearFile }) => (
        <div className="group relative h-full">
          {children({
            currentImageUrl,
            openUploadModal,
            clearImage: clearFile,
          })}
          {(uploaderUrl ?? currentImageUrl) && (
            <RemoveButton onRemove={() => handleImageRemove(clearFile)} />
          )}
        </div>
      )}
    </MultipartFileUploader>
  );
}

interface RemoveButtonProps {
  onRemove: () => Promise<boolean>;
}

function RemoveButton({ onRemove }: Readonly<RemoveButtonProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const t = useTranslations("UploaderWrapper");

  const handleRemove = async () => {
    setIsRemoving(true);
    (await onRemove()) && setIsOpen(false);
    setIsRemoving(false);
  };

  return (
    <>
      <div className="absolute top-0 right-0">
        <Button color="red" className="size-7" onClick={() => setIsOpen(true)}>
          <XMarkIcon />
        </Button>
      </div>

      <Alert open={isOpen} onClose={() => setIsOpen(false)}>
        <AlertTitle>{t("removeImage")}</AlertTitle>
        <AlertDescription>{t("removeImageConfirm")}</AlertDescription>
        <AlertActions>
          <Button plain onClick={() => setIsOpen(false)}>
            {t("cancel")}
          </Button>
          <Button color="red" onClick={handleRemove} disabled={isRemoving}>
            {isRemoving ? t("removing") : t("remove")}
          </Button>
        </AlertActions>
      </Alert>
    </>
  );
}
